import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
    MapContainer,
    TileLayer,
    useMapEvents
} from "react-leaflet";
import L, { map } from "leaflet";
import "leaflet/dist/leaflet.css";
import { useSelector, useDispatch } from "react-redux";
import {
    getMapSettings,
    getLockedRooms,
    getAllRoomsData,
    roomsIdHaveEvent,
} from "../../store/MapDisplayThunks";
import {
    selectRoomsObject,
    setCurrentRoom,
    setFocusedAvatarId,
    setIsMapReady,
} from '../../store/MapDisplaySlice';
import "./MapDisplay.css";
import "./AvatarStyles.css";
import lockIconUrl from "../../../../assets/lock-static.svg";
import Chairs from './Chair/Chairs.jsx';
import Avatars from './Avatar/Avatars.jsx';
import { selectChairsObject } from '../../store/Chairs/ChairsSlice.js';
import Rooms from './room/Rooms.jsx';
import { joinRoom } from "../../../ControlPanel/store/interact/interactThunks";
import { registerWebPush } from "../../../../utils/webPushClient.js";
import LoadingScreen from '../../../Auth/components/LoadingScreen/LoadingScreen.js';
import { getBookingMapId } from '../../../ControlPanel/store/AddEvent/AddEventThunks.js';
import ActivityDetector from '../../../../components/ActivityDetector.jsx';
import { keysEqual } from '../../../../utils/equalityFunctions.js';
import { se } from 'date-fns/locale';
import MapObjects from '../MapObject/MapObjects.jsx';
import CoffePauseProgress from '../../../CoffeePause/components/coffePauseProgress/coffePauseProgress.js';
import SessionProgress from '../../../ProductivitySession/components/SessionProgress/SessionProgress.jsx';
import CreateSession from '../../../ProductivitySession/components/CreateSession/CreateSession.jsx';
import CibleCliquable from '../MapObject/CibleCliquable.jsx';
import Agents from './Avatar/Agents.jsx';

import { triggerRecordingStop } from '../../../ScreenRecording/hooks/useRoomRecording.js';


export default function MapDisplay({ children }) {
    const [zoomLevel, setZoomLevel] = useState(1);
    const [markerOpacity, setMarkerOpacity] = useState(1);
    const initRef = useRef(false);
    const initCenterMapRef = useRef(false);
    const mapRef = useRef(null);

    useEffect(() => {
        if (mapRef.current) {
          window.leafletMap = mapRef.current;
        }
      }, []);
    
    const dispatch = useDispatch();
    const {
        mapConfig,
        zoomed,
        roomsData,
        reservedRooms,
        lockedRooms,
        updateRoom,
        currentRoom,
        loader,
        focusedAvatarId,
        maps
    } = useSelector((state) => state.MapDisplay);

    const {
        localAvatar, tenantId
    } = useSelector((state) => state.auth);

    const recorder = useSelector(
        (state) => state.recorder
      );

      const previousRoomIdRef = useRef(
        currentRoom?.room_id
      );

    const [error, setError] = useState();

    const fullScreenStyle = {
        width: "100%",
        height: "100%",
    };

    const ChairsObject = useSelector(selectChairsObject);
    const RoomsObject = useSelector(selectRoomsObject);


    const currentRoomRef = useRef(currentRoom);



    useEffect(() => {
        currentRoomRef.current = currentRoom;
    }, [currentRoom]);

    useEffect(() => {
        if (localAvatar && localAvatar.chair_id) {
            // Get the room ID for the current chair
            const chair = ChairsObject[localAvatar.chair_id];
            if (chair && chair.room_id) {
                const room = RoomsObject[chair.room_id];
                if (room) {
                    // Only set current room if it's different from the existing one
                    if (room?.room_id !== currentRoomRef.current?.room_id) dispatch(setCurrentRoom(room));
                } else {
                    console.error("Room not found for chair:", chair);
                }
            }
        }

    }, [localAvatar, ChairsObject, RoomsObject,]);


    useEffect(() => {
        const previousRoomId = previousRoomIdRef.current;
        const currentRoomId = currentRoom?.room_id;
    
        const roomChanged = previousRoomId && currentRoomId && previousRoomId !== currentRoomId;
        if (roomChanged && recorder?.isOwner && recorder?.status === 'recording') {
          triggerRecordingStop();
        }
        previousRoomIdRef.current = currentRoomId;
    
    }, [ currentRoom?.room_id, recorder?.status, recorder?.isOwner]);



    const zoomIn = useCallback(() => {
        try {
            if (mapRef.current != null && zoomLevel < mapConfig.maxZoom) {
                const map = mapRef.current;
                const zoom = map.getZoom();
                map.setView(map.getCenter(), zoom + 1);
            }
        } catch (e) {
            console.error(e);
        }
    }, [zoomLevel, mapConfig]);
    const zoomOut = useCallback(() => {
        try {
            if (mapRef.current != null && zoomLevel > mapConfig.minZoom) {
                const map = mapRef.current;
                const zoom = map.getZoom();
                map.setView(map.getCenter(), zoom - 1);
            }
        } catch (e) {
            console.error(e);
        }
    }, [zoomLevel, mapConfig]);

    const MapEvents = () => {
        useMapEvents({
            dragstart: () => {
                setMarkerOpacity(0);
            },
            dragend: () => {
                setMarkerOpacity(1);
            },
            zoomend(e) {
                setZoomLevel(e.target.getZoom());
            },

        });
    };



    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            setError();
            return;
        }
        setError();
    };

    useEffect(() => {
        const initializeComponent = async () => {
            try {
                dispatch(getMapSettings());
                dispatch(roomsIdHaveEvent());
                dispatch(getLockedRooms());
            } catch (error) {
                console.error('cant get settings', error);
            }
        }
        if (!initRef.current) {
            initRef.current = true;
            initializeComponent();

        }
    }, []);

    useEffect(() => {
        if (mapConfig.mapId) {
            dispatch(getAllRoomsData());
        }
    }, [mapConfig])
    useEffect(() => {
        dispatch(roomsIdHaveEvent());
    }, [updateRoom])


    useEffect(() => {
        try {
            if (mapRef.current && mapConfig?.mapId) {
                mapRef.current.setMinZoom(mapConfig.minZoom);
                mapRef.current.setMaxZoom(mapConfig.maxZoom);
                mapRef.current._container.style.backgroundColor = mapConfig.backgroundColor;

                const {
                    south_west_lat, south_west_lng,
                    north_east_lat, north_east_lng,
                    max_south_west_lat, max_south_west_lng,
                    max_north_east_lat, max_north_east_lng
                } = mapConfig;

                const hasBounds = [south_west_lat, south_west_lng, north_east_lat, north_east_lng].every(coord => typeof coord === 'number');
                const hasMaxBounds = [max_south_west_lat, max_south_west_lng, max_north_east_lat, max_north_east_lng].every(coord => typeof coord === 'number');

                if (hasBounds) {
                    mapRef.current.fitBounds([
                        [mapConfig.south_west_lat, mapConfig.south_west_lng],
                        [mapConfig.north_east_lat, mapConfig.north_east_lng],
                    ]);
                }
                if (hasMaxBounds) {
                    mapRef.current.setMaxBounds([
                        [max_south_west_lat, max_south_west_lng],
                        [max_north_east_lat, max_north_east_lng]
                    ]);
                }
            }
        } catch (e) {
            console.error(e);
        }
    }, [mapConfig]);




    const onCenterIconClick = () => {
        dispatch(setFocusedAvatarId(localAvatar?.id))
    }



    const centerMap = useCallback((chair, currentZoom = false) => {
        try {
            if (mapRef.current != null) {
                if (chair && chair.lat && chair.lng) {
                    const zoom = currentZoom ? mapRef.current.getZoom() : (mapConfig.defaultZoom || 1);
                    console.log("Map centered at:", chair.lat + ", " + chair.lng, "Zoom:", zoom, "Chair:", chair);
                    mapRef.current.setView([chair.lat, chair.lng], zoom);
                } else {
                    console.error("Chair not provided for centering map",);
                }
            }
        } catch (error) {
            console.error("Error centering map:", error);
        }
    }, [mapConfig]);

    const currentChairRef = useRef(null);
    useEffect(() => {
        currentChairRef.current = ChairsObject[localAvatar?.chair_id] || null;
    }, [ChairsObject, localAvatar]);

    const isFocusedAvatarIdRef = useRef(focusedAvatarId);

    useEffect(() => {
        isFocusedAvatarIdRef.current = focusedAvatarId;
    }, [focusedAvatarId]);


    const setCurrentFocusedAvatar = useCallback(() => {
        dispatch(setFocusedAvatarId(localAvatar?.id));
    }, [localAvatar]);

    const setCurrentFocusedAvatarRef = useRef(setCurrentFocusedAvatar);

    useEffect(() => {
        setCurrentFocusedAvatarRef.current = setCurrentFocusedAvatar;
    }, [setCurrentFocusedAvatar]);

    // Recenter map when the map changes, unless another centering target is active
    const prevChairsObjectRef = useRef(ChairsObject);

    useEffect(() => {
        const prevChairsObject = prevChairsObjectRef.current;
        prevChairsObjectRef.current = ChairsObject;

        // If ChairsObject has new keys, we consider it changed
        const chairsObjectChanged = !keysEqual(prevChairsObject, ChairsObject);
        // Only recenter if no other centering target is taking priority
        if (!isFocusedAvatarIdRef.current && chairsObjectChanged)
            setCurrentFocusedAvatarRef.current();
        // We know the map has changed when the ChairsObject is updated
    }, [ChairsObject]);

    

    const handleInactivity = useCallback(() => {
        centerMap(ChairsObject[localAvatar.chair_id] || null, true);
    }, [ChairsObject, localAvatar.chair_id, centerMap]);


const autoJoinExecutedRef = useRef(false);
    useEffect(() => {
        const autojoinRoomId = sessionStorage.getItem("autojoin_room_id");
        const autojoinBookingId = sessionStorage.getItem("autojoin_booking_id");
        const target = Number(autojoinRoomId);

        if (!target || !localAvatar?.id || autoJoinExecutedRef.current) return;
        autoJoinExecutedRef.current = true;
        (async () => {
            try {
                let bookingMapId;

                if (autojoinBookingId) {
                    bookingMapId = await dispatch(
                        getBookingMapId({ params: [{ bookingId: autojoinBookingId }] })
                    ).unwrap();
                }

                console.info("[MapDisplay]AutoJoin → target room_id:", target);

                const res = await dispatch(
                    joinRoom({ body: { targetRoomId: target } })
                ).unwrap();
                     console.log("joinRoom from MapDispaly")
                console.info("[MapDisplay] joinRoom success:", res);

                if (Object.keys(RoomsObject).length > 0) {
                    console.log("[MapDisplay] Searching room in RoomsObject with room_id =", target);
                    let room = Object.values(RoomsObject).find(r => r.room_id === target);

                    if (room) {
                        console.log("[MapDisplay] AutoJoin found room:", room);
                        dispatch(setCurrentRoom(room));
                    } else {
                        console.error("[MapDisplay] No room found for target room_id:", target);
                        console.log("[MapDisplay] Available room_ids:", Object.values(RoomsObject).map(r => r.room_id));
                    }
                }
                setTimeout(() => {
                    if (currentRoomRef.current?.room_id !== target) {
                        console.warn("[MapDisplay] Fallback triggered for room_id:", target);

                        let room = Object.values(RoomsObject).find(r => r.room_id === target);

                        if (room) {
                            console.log("[MapDisplay] Fallback found room:", room);
                            dispatch(setCurrentRoom(room));
                            if (currentChairRef.current) {
                                console.log("[MapDisplay] Centering map on chair:", currentChairRef.current);
                                centerMap(currentChairRef.current);
                            }
                        } else { 
                            console.error("[MapDisplay] ❌ Fallback: still no room found for room_id:", target);
                            console.log("[MapDisplay] Available room_ids:", Object.values(RoomsObject).map(r => r.room_id));
                        }
                    }
                }, 1000);

                sessionStorage.removeItem("autojoin_room_id");
                sessionStorage.removeItem("autojoin_booking_id");
            } catch (err) {
                console.error("[MapDisplay] joinRoom failed:", err);
                autoJoinExecutedRef.current = false;
            }
        })();
    }, [localAvatar?.id, RoomsObject]);


   

useEffect(() => {
        if (localAvatar?.email) {
            console.info("[MapDisplay] Initialisation WebPush pour:", localAvatar.email);
            registerWebPush(localAvatar.email);
        }
    }, [localAvatar?.email, tenantId]);


    return (<>{(mapConfig?.mapId && !loader) ?
        (<div style={fullScreenStyle}>
            <div id="map-container" >
                <MapContainer
                    ref={mapRef}
                    center={[0, 0]}
                    zoom={1}
                    minZoom={mapConfig.minZoom}
                    maxZoom={mapConfig.maxZoom}
                    crs={L.CRS.Simple}
                    style={{
                        height: "100vh",
                        width: "100vw",
                        backgroundColor: `${mapConfig.backgroundColor}`,
                    }}
                    maxBounds={mapConfig.maxBounds}
                   /*
                    whenReady={async (e) => {
                        setZoomLevel(e.target?.getZoom());
                    }}*/
                        whenReady={(e) => {
                            setZoomLevel(e.target?.getZoom());
                            window.leafletMap = e.target;
                          }}
                >
                    <TileLayer
                        url={`https://maps.peoplmap.com/${mapConfig?.path}/{z}/{x}/{y}.png`}
                        bounds={mapConfig?.bounds}
                    />
                    <MapEvents />
                    <Rooms
                        roomsData={roomsData}
                        mapId={mapConfig?.mapId}
                        mapRef={mapRef}
                        mapConfig={mapConfig}
                        markerOpacity={markerOpacity}
                        reservedRooms={reservedRooms}
                        lockedRooms={lockedRooms}
                        lockIconUrl={lockIconUrl}
                        zoomLevel={zoomLevel}
                    ></Rooms>
                    <Chairs
                        mapId={mapConfig?.mapId}
                        avatarBounds={mapConfig.avatarBounds}
                        mapRef={mapRef}
                        clickable={markerOpacity === 1}
                    />
                    <Avatars
                        onCenterMap={centerMap}
                        avatarBounds={mapConfig?.avatarBounds}
                        mapRef={mapRef}
                        zoomLevel={zoomLevel}
                     mapConfig={mapConfig}

                    />
                 <Agents
                        onCenterMap={centerMap}
                        avatarBounds={mapConfig?.avatarBounds}
                        mapRef={mapRef}
                        zoomLevel={zoomLevel}
                     mapConfig={mapConfig}

                    />
                    <MapObjects
                        onCenterMap={centerMap}
                        avatarBounds={mapConfig?.avatarBounds}
                        mapRef={mapRef}
/>
<CibleCliquable/>

                    {zoomed === false && (
                        <div className="icon-container zoom-controller">
                            <div className="zoom-in-icon" onClick={zoomIn}>
                                <span className="material-symbols-outlined">
                                    zoom_in
                                </span>
                            </div>
                            <div className="zoom-out-icon" onClick={zoomOut}>
                                <span className="material-symbols-outlined">
                                    zoom_out
                                </span>
                            </div>
                            <div className="center-icon" onClick={onCenterIconClick}>
                                <span className="material-symbols-outlined">
                                    distance
                                </span>
                            </div>
                        </div>
                    )}

                </MapContainer>
                <ActivityDetector
                    onInactivity={handleInactivity}
                    inactiveTimeout={30000}
                />
            </div>
            {children}
        </div>) : <LoadingScreen />}




        
        </>
    );

};
